/**
 * Light, dark, or whatever the operating system says.
 *
 * "System" is the default because most people have already made this choice
 * once, at the OS level, and a product that ignores it is the odd one out.
 * The class goes on <html> so Tailwind's `dark:` variants and the neutral
 * CSS variables in index.css both key off the same thing.
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type ThemeMode = 'light' | 'dark' | 'system';

const safeStorage = {
  getItem: (k: string) => { try { return localStorage.getItem(k); } catch { return null; } },
  setItem: (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* private mode */ } },
  removeItem: (k: string) => { try { localStorage.removeItem(k); } catch { /* private mode */ } },
};

const prefersDark = () => {
  try { return window.matchMedia('(prefers-color-scheme: dark)').matches; } catch { return false; }
};

export const resolveTheme = (mode: ThemeMode): 'light' | 'dark' =>
  mode === 'system' ? (prefersDark() ? 'dark' : 'light') : mode;

/** The single place the class is written, so nothing can disagree about it. */
export function applyTheme(mode: ThemeMode) {
  const resolved = resolveTheme(mode);
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
  root.style.colorScheme = resolved;
}

interface ThemeState {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  /** light → dark → light. "System" is chosen explicitly, not cycled into. */
  toggle: () => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      mode: 'system',
      setMode: (mode) => { applyTheme(mode); set({ mode }); },
      toggle: () => {
        const next: ThemeMode = resolveTheme(get().mode) === 'dark' ? 'light' : 'dark';
        applyTheme(next);
        set({ mode: next });
      },
    }),
    {
      name: 'grc-theme',
      storage: createJSONStorage(() => safeStorage),
      onRehydrateStorage: () => (state) => applyTheme(state?.mode ?? 'system'),
    },
  ),
);

/**
 * Follow the OS while the viewer has not pinned a preference. Registered once
 * at module load rather than in a component, so it survives route changes.
 */
try {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (useThemeStore.getState().mode === 'system') applyTheme('system');
  });
} catch { /* older browsers: the initial resolution still applies */ }
